import { memo, type KeyboardEvent, type MouseEvent } from "react";

import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { STOREFRONT_TRANSITION_DURATION } from "@tablescape/constants";
import { type TTailwindString, tw } from "classnames";

export type ColorSwatchViewProps = {
  active: boolean;
  alt?: string;
  ariaDescribedBy?: string;
  ariaHidden?: boolean;
  ariaLabel?: string;
  available?: boolean;
  backordered?: boolean;
  checked: boolean;
  children?: React.ReactNode;
  color?: TTailwindString;
  hex?: Maybe<string>;
  image?: string;
  interactive?: boolean;
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  optionLabel?: Maybe<string>;
  role?: string;
  sizeClass: TTailwindString;
  tabIndex?: number;
};

const styles = {
  alt: {
    line: tw("sr-only"),
  },
  container: tw("relative", "box-border", "m-0", "p-0"),
  optionLabel: {
    badge: tw(
      "flex",
      "items-center",
      "justify-center",
      "absolute",
      "-bottom-1",
      "w-full",
      "min-w-0"
    ),
    text: tw(
      "px-1",
      "py-0.5",
      "flex",
      "rounded-xl",
      "bg-background-primary-inverse",
      "text-primary-inverse",
      "font-calibre",
      "text-3xs",
      "font-semibold",
      "uppercase",
      "leading-none",
      "tracking-wide",
      "antialiased",
      "min-w-0",
      "max-w-full"
    ),
  },
  size: {
    tiny: tw("w-6", "h-6"),
    mini: tw("w-[30px]" as TTailwindString, "h-[30px]" as TTailwindString),
    regular: tw("w-[38px]" as TTailwindString, "h-[38px]" as TTailwindString),
  },
  stock: {
    container: tw(
      "box-border",
      "p-[1px]" as TTailwindString,
      "flex",
      "items-center",
      "justify-center",
      "top-0",
      "left-0",
      "absolute",
      "w-full",
      "h-full",
      "pointer-events-none"
    ),
    line: tw(
      "w-[1px]" as TTailwindString,
      "h-full",
      "bg-background-primary-inverse",
      "rotate-45"
    ),
  },
  swatch: {
    innerRing: ({
      color,
      image,
    }: {
      color?: TTailwindString;
      image?: string;
    }) =>
      tw(
        "box-border",
        "m-0",
        "p-0",
        "flex",
        "items-center",
        "justify-center",
        "border-solid",
        "border-3",
        "border-transparent",
        "rounded-full",
        "w-full",
        "h-full",
        "bg-clip-padding",
        {
          [tw(
            "outline" as TTailwindString,
            "outline-1" as TTailwindString,
            "-outline-offset-4" as TTailwindString,
            "outline-interactive-white-pressed" as TTailwindString
          )]: color === ("bg-product-clear" as TTailwindString),
          [tw(color)]: !!color,
          "bg-cover": !!image,
        }
      ),
    outerRing: ({
      active,
      available,
      backordered,
      checked,
      sizeClass,
    }: {
      active: boolean;
      available: boolean;
      backordered: boolean;
      checked: boolean;
      sizeClass: TTailwindString;
    }) =>
      tw(
        "group",
        "box-border",
        "m-0",
        "p-0",
        "transition-all",
        STOREFRONT_TRANSITION_DURATION,
        {
          "border-solid": active || checked || !available || !backordered,
          "border-dashed": !active && !checked && available && backordered,
        },
        "border",
        "hover:border-char-500",
        "cursor-pointer",
        "rounded-full",
        sizeClass,
        {
          "border-transparent":
            !active && !checked && (!available || !backordered),
          "border-primary": active || checked || backordered,
        }
      ),
  },
};

const ColorSwatchViewBase = ({
  active,
  alt,
  ariaDescribedBy,
  ariaHidden,
  ariaLabel,
  available = true,
  backordered = false,
  checked,
  children,
  color,
  hex,
  image,
  interactive = true,
  onClick,
  onKeyDown,
  optionLabel,
  role = "button",
  sizeClass,
  tabIndex,
}: ColorSwatchViewProps) => {
  const labelId = optionLabel ? `${optionLabel}-badge` : undefined;
  const ariaStateProps: React.AriaAttributes = {};
  if (role === "button") {
    ariaStateProps["aria-pressed"] = interactive
      ? checked
        ? "true"
        : "false"
      : undefined;
  } else if (role === "radio") {
    ariaStateProps["aria-checked"] = interactive
      ? checked
        ? "true"
        : "false"
      : undefined;
  }
  return (
    <div className={styles.container}>
      <div
        aria-describedby={ariaDescribedBy ?? labelId}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        {...ariaStateProps}
        className={styles.swatch.outerRing({
          active,
          available,
          backordered,
          checked,
          sizeClass,
        })}
        onClick={interactive ? onClick : undefined}
        onKeyDown={interactive ? onKeyDown : undefined}
        role={interactive ? role : undefined}
        tabIndex={interactive ? tabIndex ?? -1 : undefined}
      >
        <div
          className={styles.swatch.innerRing({ color, image })}
          {...(!!hex && { style: { backgroundColor: hex } })}
          {...(!!image && { style: { backgroundImage: `url("${image}")` } })}
        >
          {children}
          <span className={styles.alt.line}>{alt}</span>
        </div>
      </div>
      {!available && (
        <div className={styles.stock.container}>
          <span className={styles.stock.line}></span>
        </div>
      )}
      {available && optionLabel && (
        <div
          className={styles.optionLabel.badge}
          {...(labelId ? { id: labelId } : {})}
        >
          <span className={styles.optionLabel.text}>{optionLabel}</span>
        </div>
      )}
    </div>
  );
};

export const ColorSwatchView = memo(ColorSwatchViewBase);
